import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { useAuthDialog } from '../auth/AuthDialogContext';
import { useWalletLive } from '../auth/WalletLiveContext';
import { fetchReferralOverview, type ReferralOverview } from '../api/referral';
import { formatMinor } from '../api/payments';
import { fetchPlayOptions } from '../api/preferences';
import { aznMinorToPlayMinor } from '../lib/fxDisplay';
import '../styles/referral.css';

const EXAMPLE_DEPOSIT_AZN_MINOR = 10000; // 100 AZN example in copy

function shareTelegram(url: string, text: string) {
  const u = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
  window.open(u, '_blank', 'noopener,noreferrer');
}

function shareWhatsApp(url: string, text: string) {
  const u = `https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}`;
  window.open(u, '_blank', 'noopener,noreferrer');
}

export function ReferralPage() {
  const { t, i18n } = useTranslation(['referral', 'common', 'auth']);
  const { user } = useAuth();
  const { openAuth } = useAuthDialog();
  const { wallet } = useWalletLive();
  const locale = (i18n.resolvedLanguage || i18n.language || 'az').slice(0, 2);

  const [data, setData] = useState<ReferralOverview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [fxRate, setFxRate] = useState<string | number>(1);

  const playCurrency = (wallet?.play_currency || 'AZN').toUpperCase();

  const load = useCallback(async () => {
    if (!user) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [overview, playOpts] = await Promise.all([
        fetchReferralOverview(),
        fetchPlayOptions().catch(() => null),
      ]);
      setData(overview);
      const rate = playOpts?.fx?.rates_azn_per_unit?.[playCurrency];
      if (playCurrency === 'AZN') {
        setFxRate(1);
      } else if (rate != null && Number(rate) > 0) {
        setFxRate(rate);
      } else {
        setFxRate(1);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'error');
    } finally {
      setLoading(false);
    }
  }, [user, playCurrency]);

  useEffect(() => {
    void load();
  }, [load]);

  const fmt = useCallback(
    (aznMinor: number) => formatMinor(aznMinorToPlayMinor(aznMinor, fxRate), playCurrency, locale),
    [fxRate, playCurrency, locale],
  );

  const inviteLink = useMemo(() => {
    if (!data) return '';
    if (data.link_url) return data.link_url;
    if (data.code) {
      const bot = (data.bot_username || 'Vbbplaybot').replace(/^@/, '');
      return `https://t.me/${bot}?start=ref_${encodeURIComponent(data.code)}`;
    }
    return '';
  }, [data]);

  const signupLabel = fmt(data?.signup_bonus_minor ?? 100);
  const commissionPct = data ? data.deposit_commission_bps / 100 : 20;
  const holdDays = data?.deposit_hold_days ?? 3;
  const exampleDeposit = fmt(EXAMPLE_DEPOSIT_AZN_MINOR);
  const exampleCommission = fmt(
    Math.floor((EXAMPLE_DEPOSIT_AZN_MINOR * (data?.deposit_commission_bps ?? 2000)) / 10_000),
  );
  const shareText = t('referral:shareText', {
    brand: 'VBB PLAY',
    bonus: signupLabel,
    percent: commissionPct,
    days: holdDays,
  });

  async function copyLink() {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = inviteLink;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    }
  }

  async function shareMore() {
    if (!inviteLink) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'VBB PLAY', text: `${shareText}\n${inviteLink}` });
        return;
      } catch {
        // fall through
      }
    }
    await copyLink();
  }

  if (!user) {
    return (
      <div className="page referral-page">
        <section className="referral-hero">
          <div className="referral-hero-brand">VBB PLAY</div>
          <h1>{t('referral:heroTitle')}</h1>
          <p className="referral-hero-sub">
            {t('referral:heroSub', { bonus: signupLabel, percent: commissionPct, days: holdDays })}
          </p>
        </section>
        <div className="referral-card referral-login-card">
          <p>{t('referral:loginRequired')}</p>
          <button type="button" className="btn btn-primary" onClick={() => openAuth('login')}>
            {t('auth:login', { defaultValue: 'Giriş' })}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page referral-page">
      <section className="referral-hero" aria-label="VBB PLAY">
        <div className="referral-hero-visual" aria-hidden="true">
          <span className="referral-coin referral-coin-a" />
          <span className="referral-coin referral-coin-b" />
          <span className="referral-coin referral-coin-c" />
          <div className="referral-hero-figures">
            <span className="referral-fig referral-fig-l" />
            <span className="referral-fig referral-fig-r" />
          </div>
        </div>
        <div className="referral-hero-copy">
          <div className="referral-hero-brand">VBB PLAY</div>
          <h1>{t('referral:heroTitle')}</h1>
          <p className="referral-hero-sub">
            {t('referral:heroSub', { bonus: signupLabel, percent: commissionPct, days: holdDays })}
          </p>
        </div>
      </section>

      <section className="referral-card referral-link-card">
        <h2>{t('referral:yourLink')}</h2>
        <p className="referral-link-hint">{t('referral:linkHint')}</p>
        <div className="referral-link-row">
          <input
            className="referral-link-input"
            readOnly
            value={loading ? '…' : inviteLink}
            aria-label={t('referral:yourLink')}
          />
          <button type="button" className="referral-copy-btn" onClick={() => void copyLink()} disabled={!inviteLink}>
            {copied ? t('referral:copied') : t('referral:copy')}
          </button>
        </div>

        <div className="referral-share-row" role="group" aria-label={t('referral:share')}>
          <button
            type="button"
            className="referral-share-btn telegram"
            onClick={() => shareTelegram(inviteLink, shareText)}
            disabled={!inviteLink}
          >
            <span className="referral-share-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22">
                <path
                  fill="currentColor"
                  d="M9.04 15.28 8.9 19.1c.3 0 .43-.13.59-.29l1.42-1.36 2.94 2.16c.54.3.93.14 1.08-.5l1.96-9.24h.01c.17-.8-.29-1.11-.81-.92L4.3 11.5c-.78.3-.77.74-.13.93l3.5 1.09 8.13-5.12c.38-.25.73-.11.44.14"
                />
              </svg>
            </span>
            <span>Telegram</span>
          </button>
          <button
            type="button"
            className="referral-share-btn whatsapp"
            onClick={() => shareWhatsApp(inviteLink, shareText)}
            disabled={!inviteLink}
          >
            <span className="referral-share-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22">
                <path
                  fill="currentColor"
                  d="M12.04 2C6.58 2 2.15 6.4 2.15 11.82c0 1.96.52 3.87 1.5 5.55L2 22l4.8-1.56a9.9 9.9 0 0 0 5.24 1.44h.01c5.46 0 9.89-4.4 9.89-9.82C21.94 6.4 17.5 2 12.04 2zm5.76 13.98c-.24.68-1.4 1.25-1.94 1.33-.5.07-1.13.1-1.82-.11-.42-.13-.96-.31-1.66-.61-2.92-1.26-4.82-4.2-4.97-4.4-.14-.2-1.2-1.6-1.2-3.05 0-1.45.76-2.16 1.03-2.45.27-.29.59-.36.79-.36h.57c.18 0 .42-.07.66.5.24.58.82 2 .89 2.14.07.14.12.31.02.5-.1.2-.14.32-.28.5-.14.17-.3.39-.42.52-.14.14-.28.29-.12.57.16.29.71 1.17 1.53 1.9 1.05.93 1.94 1.22 2.22 1.36.28.14.44.12.6-.07.16-.2.7-.81.89-1.09.19-.28.38-.23.64-.14.27.1 1.7.8 1.99.95.29.14.48.22.55.34.07.12.07.7-.17 1.38z"
                />
              </svg>
            </span>
            <span>WhatsApp</span>
          </button>
          <button type="button" className="referral-share-btn copy" onClick={() => void copyLink()} disabled={!inviteLink}>
            <span className="referral-share-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
                <rect x="8" y="8" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="1.8" />
                <path d="M6 15V6a2 2 0 0 1 2-2h9" stroke="currentColor" strokeWidth="1.8" />
              </svg>
            </span>
            <span>{t('referral:copy')}</span>
          </button>
          <button type="button" className="referral-share-btn more" onClick={() => void shareMore()} disabled={!inviteLink}>
            <span className="referral-share-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="20" height="20">
                <circle cx="12" cy="5" r="1.6" fill="currentColor" />
                <circle cx="12" cy="12" r="1.6" fill="currentColor" />
                <circle cx="12" cy="19" r="1.6" fill="currentColor" />
              </svg>
            </span>
            <span>{t('referral:more')}</span>
          </button>
        </div>
      </section>

      <section className="referral-info-card" aria-label={t('referral:infoTitle')}>
        <h2>{t('referral:infoTitle')}</h2>
        <ul>
          <li>{t('referral:infoSignup', { bonus: signupLabel })}</li>
          <li>{t('referral:infoBonusGames')}</li>
          <li>
            {t('referral:infoDeposit', {
              percent: commissionPct,
              exampleDeposit,
              exampleCommission,
            })}
          </li>
          <li>{t('referral:infoHold', { days: holdDays })}</li>
          <li>{t('referral:infoWithdraw')}</li>
          <li>{t('referral:infoUnique')}</li>
        </ul>
      </section>

      <section className="referral-stats-card">
        <div className="referral-stat">
          <span className="referral-stat-label">{t('referral:invitedLabel')}</span>
          <strong className="referral-stat-value">{loading ? '…' : data?.invited_count ?? 0}</strong>
          <span className="referral-stat-unit">{t('referral:friendsUnit')}</span>
        </div>
        <div className="referral-stat-divider" aria-hidden="true" />
        <div className="referral-stat">
          <span className="referral-stat-label">{t('referral:earningsLabel')}</span>
          <strong className="referral-stat-value earnings">
            {loading ? '…' : fmt(data?.earnings_total_minor ?? 0)}
          </strong>
          <span className="referral-stat-unit">
            {t('referral:earningsSplit', {
              bonus: fmt(data?.earnings_bonus_minor ?? 0),
              real: fmt(data?.earnings_real_minor ?? 0),
            })}
          </span>
          {(data?.earnings_pending_minor ?? 0) > 0 ? (
            <span className="referral-stat-pending">
              {t('referral:earningsPending', {
                amount: fmt(data?.earnings_pending_minor ?? 0),
                days: holdDays,
              })}
            </span>
          ) : null}
        </div>
      </section>

      <section className="referral-friends">
        <div className="referral-friends-head">
          <h2>{t('referral:friendsTitle')}</h2>
          <span>{t('referral:friendsCount', { count: data?.invited_count ?? 0 })}</span>
        </div>
        {error ? <p className="referral-error">{error}</p> : null}
        {!loading && (!data?.friends || data.friends.length === 0) ? (
          <p className="referral-empty">{t('referral:friendsEmpty')}</p>
        ) : (
          <ul className="referral-friends-list">
            {(data?.friends || []).map((f) => (
              <li key={f.public_id}>
                <div className="referral-friend-main">
                  <strong>{f.display_name}</strong>
                  <small>{t('referral:friendId', { id: f.short_id || f.public_id.slice(0, 8) })}</small>
                  <small>
                    {t('referral:friendDeposit', {
                      amount: fmt(f.deposit_total_minor ?? 0),
                    })}
                  </small>
                  {(f.pending_minor ?? 0) > 0 ? (
                    <small className="referral-friend-pending">
                      {t('referral:friendPending', { amount: fmt(f.pending_minor || 0) })}
                      {f.pending_available_at
                        ? ` · ${t('referral:friendPendingUntil', {
                            date: new Date(f.pending_available_at).toLocaleDateString(locale),
                          })}`
                        : ''}
                    </small>
                  ) : null}
                </div>
                <div className="referral-friend-side">
                  <span>{t('referral:friendEarned', { amount: fmt(f.earned_minor) })}</span>
                  {f.joined_at ? <small>{new Date(f.joined_at).toLocaleDateString(locale)}</small> : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
